import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  type User as FirebaseUser,
  type MultiFactorResolver,
  type MultiFactorError,
} from 'firebase/auth';
import { auth } from './firebase.js';
import type { Role, Category } from '@sfp/shared/roles';
import { ROLE_DEFAULT_PERMISSIONS } from '@sfp/shared/permissions';

const DEMO_MODE = import.meta.env.VITE_SFP_DEMO_MODE === 'true';

interface SfpClaims {
  role?: Role;
  allowedCategories?: Category[];
  allowedTileGroupIds?: string[];
  allowedSidebarItemIds?: string[];
  allowedTopActionIds?: string[];
  allowedTopicIds?: string[];
}

export interface MfaChallenge {
  resolver: MultiFactorResolver;
  /** Which second factor we're challenging with. */
  factor: 'sms' | 'totp';
  /** Phone-SMS only: the verification id from the sent code. */
  verificationId?: string;
  /** TOTP only: the enrolled factor's uid. */
  totpUid?: string;
  /** Display hint — last-4 of the phone, or 'authenticator app'. */
  hint: string;
}

interface AuthState {
  user: FirebaseUser | null;
  role: Role | null;
  allowedCategories: Category[];
  allowedTileGroupIds: string[];
  allowedSidebarItemIds: string[];
  allowedTopActionIds: string[];
  allowedTopicIds: string[];
  loading: boolean;
  /** True if the signed-in user has at least one enrolled MFA factor. */
  mfaEnrolled: boolean;
  /** Set after a sign-in step throws auth/multi-factor-auth-required. */
  mfaChallenge: MfaChallenge | null;
  signIn: (email: string, password: string, recaptchaContainerId: string) => Promise<void>;
  signInWithGoogle: (recaptchaContainerId: string) => Promise<void>;
  completeMfa: (code: string) => Promise<void>;
  cancelMfa: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

let recaptcha: RecaptchaVerifier | null = null;
function ensureRecaptcha(containerId: string): RecaptchaVerifier {
  // The verifier MUST be a singleton per container — re-creating it triggers
  // "reCAPTCHA has already been rendered" errors.
  if (recaptcha) return recaptcha;
  recaptcha = new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
  return recaptcha;
}

function clearRecaptcha() {
  try {
    recaptcha?.clear();
  } catch {
    /* ignore */
  }
  recaptcha = null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [allowedCategories, setAllowedCategories] = useState<Category[]>([]);
  const [allowedTileGroupIds, setAllowedTileGroupIds] = useState<string[]>([]);
  const [allowedSidebarItemIds, setAllowedSidebarItemIds] = useState<string[]>([]);
  const [allowedTopActionIds, setAllowedTopActionIds] = useState<string[]>([]);
  const [allowedTopicIds, setAllowedTopicIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);

  useEffect(() => {
    if (DEMO_MODE) {
      setUser({
        uid: 'demo-uid',
        email: 'demo@sfp.local',
        displayName: 'Demo Admin',
        getIdTokenResult: async () => ({ claims: { role: 'admin' } }),
      } as unknown as FirebaseUser);
      setRole('admin' as Role);
      setMfaEnrolled(true);
      setLoading(false);
      return;
    }
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Force-refresh so the freshest custom claims (role, permissions) land
        // in the token. Without this, a session minted before an admin claim
        // was granted keeps treating the user as non-admin — which silently
        // restricts Firestore queries (e.g. Documents shows empty).
        const tokenResult = await u.getIdTokenResult(true);
        const claims = tokenResult.claims as SfpClaims;
        const userRole = claims.role ?? null;
        setRole(userRole);
        setAllowedCategories((claims.allowedCategories ?? []) as Category[]);
        const def = userRole ? ROLE_DEFAULT_PERMISSIONS[userRole] : null;
        setAllowedTileGroupIds(
          (claims.allowedTileGroupIds && claims.allowedTileGroupIds.length > 0
            ? claims.allowedTileGroupIds
            : def?.allowedTileGroupIds) ?? [],
        );
        setAllowedSidebarItemIds(
          (claims.allowedSidebarItemIds && claims.allowedSidebarItemIds.length > 0
            ? claims.allowedSidebarItemIds
            : def?.allowedSidebarItemIds) ?? [],
        );
        setAllowedTopActionIds(
          (claims.allowedTopActionIds && claims.allowedTopActionIds.length > 0
            ? claims.allowedTopActionIds
            : def?.allowedTopActionIds) ?? [],
        );
        setAllowedTopicIds(
          (claims.allowedTopicIds && claims.allowedTopicIds.length > 0
            ? claims.allowedTopicIds
            : def?.allowedTopicIds) ?? [],
        );
        try {
          setMfaEnrolled(multiFactor(u).enrolledFactors.length > 0);
        } catch {
          setMfaEnrolled(false);
        }
      } else {
        setRole(null);
        setAllowedCategories([]);
        setAllowedTileGroupIds([]);
        setAllowedSidebarItemIds([]);
        setAllowedTopActionIds([]);
        setAllowedTopicIds([]);
        setMfaEnrolled(false);
      }
      setLoading(false);
    });
  }, []);

  async function handleMfaError(err: unknown, containerId: string): Promise<void> {
    if (!isMfaError(err)) throw err;
    const resolver = getMultiFactorResolver(auth, err);

    // Prefer TOTP when enrolled — no SMS send, no reCAPTCHA, no carrier/region
    // dependency; the user just reads the code from their authenticator app.
    const totpHint = resolver.hints.find(
      (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    if (totpHint) {
      setMfaChallenge({ resolver, factor: 'totp', totpUid: totpHint.uid, hint: 'authenticator app' });
      return;
    }

    const phoneHint = resolver.hints.find(
      (h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
    );
    if (!phoneHint) {
      throw new Error('No supported second factor is enrolled for this account.');
    }
    const verifier = ensureRecaptcha(containerId);
    const verificationId = await new PhoneAuthProvider(auth).verifyPhoneNumber(
      { multiFactorHint: phoneHint, session: resolver.session },
      verifier,
    );
    setMfaChallenge({
      resolver,
      factor: 'sms',
      verificationId,
      hint: (phoneHint as any).phoneNumber?.slice(-4) ?? '••',
    });
  }

  const signIn = async (email: string, password: string, containerId: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      await handleMfaError(err, containerId);
    }
  };

  const signInWithGoogle = async (containerId: string) => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ hd: 'shulkinfamilypartners.com', prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      await handleMfaError(err, containerId);
    }
  };

  const completeMfa = async (code: string) => {
    if (!mfaChallenge) throw new Error('No MFA challenge in flight');
    const assertion =
      mfaChallenge.factor === 'totp'
        ? TotpMultiFactorGenerator.assertionForSignIn(mfaChallenge.totpUid!, code)
        : PhoneMultiFactorGenerator.assertion(
            PhoneAuthProvider.credential(mfaChallenge.verificationId!, code),
          );
    await mfaChallenge.resolver.resolveSignIn(assertion);
    setMfaChallenge(null);
    clearRecaptcha();
  };

  const cancelMfa = () => {
    setMfaChallenge(null);
    clearRecaptcha();
  };

  const signOut = async () => {
    await fbSignOut(auth);
    clearRecaptcha();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        allowedCategories,
        allowedTileGroupIds,
        allowedSidebarItemIds,
        allowedTopActionIds,
        allowedTopicIds,
        loading,
        mfaEnrolled,
        mfaChallenge,
        signIn,
        signInWithGoogle,
        completeMfa,
        cancelMfa,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function isMfaError(err: unknown): err is MultiFactorError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: string }).code === 'auth/multi-factor-auth-required'
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useCanSeeCategory(category: Category): boolean {
  const { role, allowedCategories } = useAuth();
  if (role === 'admin') return true;
  return allowedCategories.includes(category);
}
